import {BarcodeScannerPopUp} from '@hconnect/common/components/barScanner/BarcodeScannerPopUp'
import {FormIconButton} from '@hconnect/common/components/formFields'
import {useEquipment} from '@hconnect/common/hooks/useEquipment'
import {
  EquipmentData,
  HttpError,
  Stoppage,
  EquipmentPlain,
  EquipmentIdType
} from '@hconnect/common/types'
import {useIsMobile} from '@hconnect/uikit'
import {spawnErrorToast} from '@hm/fundamental'
import QrCodeIcon from '@mui/icons-material/QrCode'
import {Box, Typography} from '@mui/material'
import {AxiosError} from 'axios'
import {isUndefined} from 'lodash'
import {ReactNode, useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_SHIFT_EVENT_WIDGET_NAMESPACE} from '../../consts'

import {EventEquipmentSearchHierarchy} from './EventEquipmentSearchHierarchy'
import {EventMainEquipmentSearchHierarchy} from './EventMainEquipmentSearchHierarchy'

const EQUIPMENT_CONTAINER_SX = {
  display: 'flex',
  '&:not(:last-child)': {
    paddingBottom: 1
  },
  '&:last-child': {
    paddingTop: 1
  },
  gap: 2
}

type EquipmentSearchContainerProps = {
  label?: ReactNode
  equipmentNumberErrorText?: ReactNode
  mainEquipmentNumbersErrorText?: ReactNode
  updateMultiPart: (delta: Partial<Stoppage>) => void
  plantId: string
  isFunctionalLocationRequired?: boolean
  isMainEquipmentRequired?: boolean
  value: Stoppage
  mainEquipmentDisabled?: boolean
  equipmentDisabled?: boolean
  equipmentIdType?: EquipmentIdType
  hideFunctionalLocation?: boolean
}

export const EquipmentSearchContainer = (props: EquipmentSearchContainerProps) => {
  const {t, i18n} = useTranslation(COMMON_SHIFT_EVENT_WIDGET_NAMESPACE)
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const isMobile = useIsMobile()

  const {
    updateMultiPart,
    equipmentNumberErrorText,
    mainEquipmentNumbersErrorText,
    value: item,
    isFunctionalLocationRequired,
    isMainEquipmentRequired,
    mainEquipmentDisabled,
    equipmentDisabled
  } = props

  const [equipment, setEquipment] = useState<EquipmentPlain | undefined>(
    item.equipment
      ? {
          equipment: item.equipment,
          path: []
        }
      : undefined
  )

  const [mainEquipment, setMainEquipment] = useState<EquipmentData | undefined>(
    item.mainEquipment
      ? {
          id: item.mainEquipment.id,
          idType: item.mainEquipment.idType,
          text: item.mainEquipment.text
        }
      : undefined
  )

  const updateMainEquipment = useCallback(
    (value?: EquipmentData) => {
      setMainEquipment(value)
      updateMultiPart({
        mainEquipment: value
      })
    },
    [updateMultiPart]
  )

  const updateEquipment = useCallback(
    (value?: EquipmentPlain) => {
      setEquipment(value)
      const mainEquipmentData = value?.mainEquipment
      if (!isUndefined(mainEquipmentData)) {
        updateMultiPart({
          equipment: value?.equipment,
          processStage: value?.processStage?.processStep,
          mainEquipment: mainEquipmentData
        })
        setMainEquipment(mainEquipmentData)
      } else {
        updateMultiPart({
          equipment: value?.equipment,
          processStage: value?.processStage?.processStep
        })
      }
    },
    [updateMultiPart]
  )

  const [searchTerm, setSearchTerm] = useState<string | undefined>()
  useEquipment(props.plantId, searchTerm || '', 'sapNumber', i18n.language, {
    enabled: !!searchTerm,
    onSuccess: (item) => {
      if (item) {
        updateEquipment(item)
        setSearchTerm(undefined)
      }
    },
    onError: (error) => {
      const typedError = error as AxiosError<HttpError>
      if (typedError.response?.data.detail) {
        spawnErrorToast(typedError.response.data.detail)
        setSearchTerm(undefined)
      }
    }
  })

  return (
    <>
      <Typography variant="h5" fontWeight={600} mb={3.5}>
        {t('shiftEvent.label.rootCausingEquipment')}
      </Typography>
      {!props.hideFunctionalLocation && (
        <Box sx={EQUIPMENT_CONTAINER_SX}>
          <Box flex={1}>
            <EventEquipmentSearchHierarchy
              data-test-id="equipment-search-dropdown"
              errorText={equipmentNumberErrorText}
              onChange={(value) => updateEquipment(value)}
              value={equipment}
              required={isFunctionalLocationRequired}
              disabled={equipmentDisabled}
              plantId={props.plantId}
              mainEquipmentId={mainEquipment?.id}
              idType={props.equipmentIdType}
            />
          </Box>
          {isMobile && (
            <>
              <FormIconButton
                data-test-id="equipment-search-container-scan-button"
                onClick={() => setIsScannerOpen(!isScannerOpen)}
                disabled={equipmentDisabled}
              >
                <QrCodeIcon />
              </FormIconButton>

              <BarcodeScannerPopUp
                open={isScannerOpen}
                onClose={() => setIsScannerOpen(false)}
                onScan={setSearchTerm}
              />
            </>
          )}
        </Box>
      )}
      <Box sx={EQUIPMENT_CONTAINER_SX}>
        <Box flex={1}>
          <EventMainEquipmentSearchHierarchy
            plantId={props.plantId}
            errorText={mainEquipmentNumbersErrorText}
            onChange={(value) => updateMainEquipment(value)}
            value={mainEquipment}
            required={isMainEquipmentRequired}
            disabled={mainEquipmentDisabled}
            idType={props.equipmentIdType}
          />
        </Box>
      </Box>
    </>
  )
}
